import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import { captureAttribution } from './lib/attribution';
import { ActingAthleteProvider } from './context/ActingAthleteContext';
import ActingAthleteFromLink from './components/ActingAthleteFromLink';
import { EntitlementsProvider } from './context/EntitlementsContext';
import { CatalogProvider } from './context/CatalogContext';
import Layout from './components/Layout';
import { RailFrame } from './components/AdminShell';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/Register';
import SignupRolePicker from './pages/SignupRolePicker';
import ParentRegister from './pages/ParentRegister';
import DashboardPage from './pages/DashboardPage';
import CardsPage from './pages/CardsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AthletesStaffPage from './pages/AthletesStaffPage';
import PartnerBillingPage from './pages/PartnerBillingPage';
import ConciergePaymentDonePage from './pages/ConciergePaymentDonePage';
import AdminPaymentsPage from './pages/AdminPaymentsPage';
import AdminMergePage from './pages/AdminMergePage';
import AdminSchoolLogosPage from './pages/AdminSchoolLogosPage';
import AdminAthleteProfilePage from './pages/AdminAthleteProfilePage';
import ConciergeNotesPage from './pages/ConciergeNotesPage';
import ConciergeBoardPage from './pages/ConciergeBoardPage';
import ConciergeTodoQueuePage from './pages/ConciergeTodoQueuePage';
import AdminMeetingNotesPage from './pages/AdminMeetingNotesPage';
import AdminInquiriesPage from './pages/AdminInquiriesPage';
import AdminEngagePage from './pages/AdminEngagePage';
import EngageTodayPage from './pages/EngageTodayPage';
import AdminFlagsPage from './pages/AdminFlagsPage';
import AdminApprovalsPage from './pages/AdminApprovalsPage';
import MessagingPage from './pages/MessagingPage';
import AthleteProfilePage from './pages/AthleteProfilePage';
import CoachPlayerSearchPage from './pages/CoachPlayerSearchPage';
import PlayerStatsPage from './pages/PlayerStatsPage';
import MySportsPage from './pages/MySportsPage';
import VideosPage from './pages/VideosPage';
import GameStatsPage from './pages/GameStatsPage';
import SettingsPage from './pages/SettingsPage';
import PhotosPage from './pages/MediaPage';
import CampsPage from './pages/CampsPage';
import CampReviewPage from './pages/CampReviewPage';
import CampCheckInPage from './pages/CampCheckInPage';
import WebinarsPage from './pages/WebinarsPage';
import RecruitPage from './pages/RecruitPage';
import RecruitRedirect from './components/recruit/RecruitRedirect';
import FollowingPage from './pages/FollowingPage';
import SchoolSearchPage from './pages/SchoolSearchPage';
import CompareAthletesPage from './pages/CompareAthletesPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ActivationSigninPage from './pages/ActivationSigninPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import GuardianAcceptPage from './pages/GuardianAcceptPage';
import GuardianWelcomePage from './pages/GuardianWelcomePage';
import GuardianManagePage from './pages/GuardianManagePage';
import ConnectAthletePage from './pages/ConnectAthletePage';
import ConfirmEmailChangePage from './pages/ConfirmEmailChangePage';
import PublicProfilePage from './pages/PublicProfilePage';
import ReportLandingPage from './pages/ReportLandingPage';
import PublicCampPage from './pages/PublicCampPage';
import PublicCampRegisterPage from './pages/PublicCampRegisterPage';
import PublicCampRegisterSuccessPage from './pages/PublicCampRegisterSuccessPage';
import PublicWebinarRegisterPage from './pages/PublicWebinarRegisterPage';
import PublicWebinarRegisterSuccessPage from './pages/PublicWebinarRegisterSuccessPage';
import CoachPublicProfilePage from './pages/CoachPublicProfilePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import SupportPage from './pages/SupportPage';
import PlayingCardPage from './pages/PlayingCardPage';
import SocialPage from './pages/SocialPage';
import AcademicsPage from './pages/AcademicsPage';
import PreferencesPage from './pages/PreferencesPage';
import CampDataPage from './pages/CampDataPage';
import CoachProfilePage from './pages/CoachProfilePage';
import AdminProfilePage from './pages/AdminProfilePage';
import CoachAcademicsPage from './pages/CoachAcademicsPage';
import CoachPhotosPage from './pages/CoachPhotosPage';
import CoachSocialPage from './pages/CoachSocialPage';
import CoachVideosPage from './pages/CoachVideosPage';
import MarketingDashboardPage from './pages/MarketingDashboardPage';
import MarketingSchoolsPage from './pages/MarketingSchoolsPage';
import MarketingPlayersPage from './pages/MarketingPlayersPage';
import CoachReviewActionPage from './pages/CoachReviewActionPage';
import AdminContentPage from './pages/AdminContentPage';
import AdminReportsPage from './pages/AdminReportsPage';
import ListsPage from './pages/ListsPage';
import CoachesPage from './pages/CoachesPage';
import CoachDetailPage from './pages/CoachDetailPage';
import TextFlowPage from './pages/TextFlowPage';
import CoachReportsPage from './pages/CoachReportsPage';
import AdminLoginsReportPage from './pages/AdminLoginsReportPage';
import AdminLoginProblemsPage from './pages/AdminLoginProblemsPage';
import AdminAiCardsPage from './pages/AdminAiCardsPage';
import AdminClientProofPage from './pages/AdminClientProofPage';
import AdminSignupsReportPage from './pages/AdminSignupsReportPage';
import AdminDripCampaignsPage from './pages/AdminDripCampaignsPage';
import AdminWristbandsPage from './pages/AdminWristbandsPage';
import SavedAthletesPage from './pages/SavedAthletesPage';
import UpgradePage from './pages/UpgradePage';
import { ATHLETE_ROLES } from './utils/roles';
import './App.css';

const NotFoundPage = () => (
  <div style={{ textAlign: 'center', padding: '80px 20px' }}>
    <h1 style={{ fontSize: '48px', color: '#1a202c', marginBottom: '8px' }}>404</h1>
    <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px' }}>Page not found</p>
    <a href="/dashboard" style={{ color: '#4D8064', fontWeight: 500 }}>Go to Dashboard</a>
  </div>
);

const useDocumentTitle = (title) => {
  React.useEffect(() => {
    document.title = title ? `${title} | ProZpects` : 'ProZpects';
  }, [title]);
};

const PageWithTitle = ({ title, children }) => {
  useDocumentTitle(title);
  return children;
};

// The standalone verify-stats page is retired — its functionality lives in
// the athlete-detail page's Measurables editor. Old links/bookmarks to
// /verify-stats/:playerId redirect there.
const VerifyStatsRedirect = () => {
  const { playerId } = useParams();
  return <Navigate to={`/athletes/${playerId}`} replace />;
};

// Saved athletes and Coach reports became Lists for admin, staff and partners (their saved athletes were
// copied into a "★ Saved athletes" list). Coaches keep both pages, so only these roles are sent on.
export const ListsForRailRoles = ({ children }) => {
  const { user } = useAuth();
  return user && ['admin', 'internal_staff', 'partner'].includes(user.role)
    ? <Navigate to="/lists" replace />
    : children;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return isAuthenticated ? <Layout>{children}</Layout> : <Navigate to="/login" replace />;
};

export const RoleProtectedRoute = ({ children, allowedRoles, denyPartnerMember = false }) => {
  const { isAuthenticated, loading, user, isPartnerMember } = useAuth();

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Team members carry role 'partner' too; pages only the partner may open turn them away.
  if (denyPartnerMember && isPartnerMember) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Social Engage Today page: admins, or any user carrying the is_engage_reviewer flag
// (coaches/teachers keep their own role). The server enforces the same rule. The flag opens
// this one route only; every /admin route stays behind RoleProtectedRoute.
export const ReviewerRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!(user && (user.role === 'admin' || user.is_engage_reviewer))) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
};

/**
 * /athletes/uid/:uid -> /athletes/:userId
 *
 * Public athlete links only ever carry the 8-digit `uid`; the coach
 * athlete-detail route is keyed by the internal users.id, and that id is
 * deliberately absent from the anonymous public payload. This is where the two
 * meet, behind the same role gate as the destination.
 */
const AthleteByUidRedirect = () => {
  const { uid } = useParams();
  const { API_URL } = useAuth();
  const [userId, setUserId] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/athletes/uid/${uid}/profile`)
      .then(res => { if (!cancelled) setUserId(res.data.user_id); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [uid, API_URL]);

  // Spec §9: the standard not-found state, not a redirect. A silent teleport to
  // the dashboard is worse than a 404 here — the coach followed a link to a
  // specific athlete and would have no way to tell whether the uid was wrong,
  // the athlete was gone, or the app simply lost them.
  if (failed) return <NotFoundPage />;
  if (userId === null) return <div className="loading-spinner">Loading...</div>;
  return <Navigate to={`/athletes/${userId}`} replace />;
};

// Partners' old My athletes page became Athletes & Staff (/partner/team); an old bookmark or
// link lands there, keeping ?add=1. Staff keep the existing AdminUsersPage.
const UsersPage = () => {
  const { user } = useAuth();
  const { search } = useLocation();
  return user?.role === 'partner'
    ? <Navigate to={`/partner/team${search}`} replace />
    : <AdminUsersPage />;
};

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PageWithTitle title="Home"><HomePage /></PageWithTitle>} />
      <Route path="/login" element={<PageWithTitle title="Log In"><LoginPage /></PageWithTitle>} />
      <Route path="/register" element={<PageWithTitle title="Sign Up"><SignupRolePicker /></PageWithTitle>} />
      <Route path="/register/athlete" element={<PageWithTitle title="Athlete Sign Up"><RegisterPage role="player" /></PageWithTitle>} />
      <Route path="/register/coach" element={<PageWithTitle title="Coach Sign Up"><RegisterPage role="coach" /></PageWithTitle>} />
      <Route path="/register/parent" element={<PageWithTitle title="Parent Sign Up"><ParentRegister /></PageWithTitle>} />
      <Route path="/privacy" element={<PageWithTitle title="Privacy Policy"><PrivacyPolicyPage /></PageWithTitle>} />
      <Route path="/terms" element={<PageWithTitle title="Terms of Use"><TermsOfUsePage /></PageWithTitle>} />
      <Route path="/support" element={<PageWithTitle title="Support"><SupportPage /></PageWithTitle>} />
      <Route path="/forgot-password" element={<PageWithTitle title="Forgot Password"><ForgotPasswordPage /></PageWithTitle>} />
      <Route path="/reminder-signin" element={<PageWithTitle title="Signing you in"><ActivationSigninPage /></PageWithTitle>} />
      <Route path="/reset-password" element={<PageWithTitle title="Reset Password"><ResetPasswordPage /></PageWithTitle>} />
      <Route path="/verify-email" element={<PageWithTitle title="Verify Email"><VerifyEmailPage /></PageWithTitle>} />
      <Route path="/guardian/accept" element={<PageWithTitle title="Accept Guardian Invite"><GuardianAcceptPage /></PageWithTitle>} />
      <Route path="/welcome" element={<PageWithTitle title="Welcome"><GuardianWelcomePage /></PageWithTitle>} />
      <Route path="/confirm-email" element={<PageWithTitle title="Confirm Email Change"><ConfirmEmailChangePage /></PageWithTitle>} />
      <Route path="/coach/:uid/:profileUrl" element={<CoachPublicProfilePage />} />
      <Route path="/athlete/:uid/:profileUrl" element={<PublicProfilePage />} />
      <Route path="/athlete/:uid/:profileUrl/card" element={<PlayingCardPage />} />
      <Route path="/athlete/:profileUrl" element={<PublicProfilePage />} />
      <Route path="/athlete/:profileUrl/card" element={<PlayingCardPage />} />
      <Route path="/report/:code" element={<ReportLandingPage />} />
      <Route path="/camp/:slug" element={<PublicCampPage />} />
      <Route path="/camp/:slug/register" element={<PublicCampRegisterPage />} />
      <Route path="/camp/:slug/success" element={<PublicCampRegisterSuccessPage />} />
      <Route path="/webinar/:slug" element={<PublicWebinarRegisterPage />} />
      <Route path="/webinar/:slug/success" element={<PublicWebinarRegisterSuccessPage />} />
      <Route path="/concierge-payment/done" element={<ConciergePaymentDonePage />} />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Upgrade"><UpgradePage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Dashboard"><DashboardPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cards"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Your player card"><CardsPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/guardian/athletes"
        element={
          <RoleProtectedRoute allowedRoles={['guardian']}>
            <PageWithTitle title="My Athletes"><GuardianManagePage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/connect-athlete"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Connect Your Athlete"><ConnectAthletePage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/team"
        element={
          <RoleProtectedRoute allowedRoles={['partner']}>
            <PageWithTitle title="Athletes & Staff"><AthletesStaffPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/partner/billing"
        element={
          <RoleProtectedRoute allowedRoles={['partner']}>
            <PageWithTitle title="Billing"><PartnerBillingPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Manage Users"><UsersPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Payments"><AdminPaymentsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/merge"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Merge Accounts"><AdminMergePage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/school-logos"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="School Logos"><AdminSchoolLogosPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/athletes/:userId"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'coach', 'partner']}>
            <PageWithTitle title="Athlete Profile"><AdminAthleteProfilePage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      {/*
        React Router v6 ranks a static path segment over a dynamic one, so
        "/athletes/uid/:uid" matches "/athletes/uid/12345678" ahead of
        "/athletes/:userId" regardless of registration order — array order
        is not what decides this. It is still declared above the
        :userId route purely for readability, so the two related routes
        read top-to-bottom as "specific case, then general case."
      */}
      <Route
        path="/athletes/uid/:uid"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'coach', 'partner']}>
            <PageWithTitle title="Athlete Profile"><AthleteByUidRedirect /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/athletes/:userId"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'coach', 'partner']}>
            <PageWithTitle title="Athlete Profile"><AdminAthleteProfilePage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/athletes/:userId/concierge"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Concierge"><ConciergeNotesPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/concierge"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Concierge"><ConciergeBoardPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/concierge/todos"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Open items"><ConciergeTodoQueuePage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/meeting-notes"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Meeting Note Exceptions"><AdminMeetingNotesPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/inquiries"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Inquiries"><AdminInquiriesPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/engage"
        element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <PageWithTitle title="Social Engage"><AdminEngagePage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/engage/today"
        element={
          <ReviewerRoute>
            <RailFrame active="mkt-instagram">
              <PageWithTitle title="Today's Instagram list"><EngageTodayPage /></PageWithTitle>
            </RailFrame>
          </ReviewerRoute>
        }
      />
      <Route
        path="/admin/approvals"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Coach Approvals"><AdminApprovalsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/flags"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Flagged Messages"><AdminFlagsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/content"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Manage Content"><AdminContentPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Reports"><AdminReportsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      {/* Keyed so College <-> High School is a fresh page: filters, search and the seen divisions
          never carry across (a college division sent as a classification empties the list). */}
      <Route
        path="/coaches"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="College Coaches"><CoachesPage key="college" /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/coaches/high-school"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="High School Coaches"><CoachesPage key="high_school" kind="high_school" /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/coaches/text"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Text coaches"><TextFlowPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/coaches/:id"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Coach"><CoachDetailPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/lists"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Lists"><ListsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      {/* The List Builder was merged into Lists (2026-10-13); keep old bookmarks working. */}
      <Route path="/admin/report-builder" element={<Navigate to="/lists" replace />} />
      <Route
        path="/admin/logins"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Logins Report"><AdminLoginsReportPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/login-problems"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Login Problems"><AdminLoginProblemsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-cards"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="AI Cards"><AdminAiCardsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/client-proof"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Client Proof"><AdminClientProofPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/signups"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Signups Report"><AdminSignupsReportPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/drip-campaigns"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Drip Campaigns"><AdminDripCampaignsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/wristbands"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Print Wristbands"><AdminWristbandsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/coach-review"
        element={<PageWithTitle title="Coach Review"><CoachReviewActionPage /></PageWithTitle>}
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Messages"><MessagingPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Profile"><AthleteProfilePage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/players"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Player Search"><CoachPlayerSearchPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/saved-athletes"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'admin', 'internal_staff', 'partner']}>
            <ListsForRailRoles><PageWithTitle title="Saved Athletes"><SavedAthletesPage /></PageWithTitle></ListsForRailRoles>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/coach-reports"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'admin', 'internal_staff', 'partner']}>
            <ListsForRailRoles><PageWithTitle title="Reports"><CoachReportsPage /></PageWithTitle></ListsForRailRoles>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/measurables"
        element={
          <RoleProtectedRoute allowedRoles={ATHLETE_ROLES}>
            <PageWithTitle title="Measurables"><PlayerStatsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/my-sports"
        element={
          <RoleProtectedRoute allowedRoles={ATHLETE_ROLES}>
            <PageWithTitle title="My Sports"><MySportsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/videos"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Videos"><VideosPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <RoleProtectedRoute allowedRoles={ATHLETE_ROLES}>
            <PageWithTitle title="Game Stats"><GameStatsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route path="/verify-stats/:playerId" element={<VerifyStatsRedirect />} />
      <Route
        path="/compare"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Compare Athletes"><CompareAthletesPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      {/* One Recruit surface: Me/Schools modes, Schools tabs. */}
      <Route
        path="/recruit"
        element={
          <RoleProtectedRoute allowedRoles={ATHLETE_ROLES}>
            <PageWithTitle title="Recruiting"><RecruitPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      {/* Old recruiting URLs land on their equivalent tab, carrying query
          params through (/matches?peek=<unitid> is still a live affordance). */}
      <Route
        path="/matches"
        element={
          <RoleProtectedRoute allowedRoles={ATHLETE_ROLES}>
            <RecruitRedirect tab="matches" />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/schools/search"
        element={
          <RoleProtectedRoute allowedRoles={ATHLETE_ROLES}>
            <PageWithTitle title="Search Schools"><SchoolSearchPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/offers"
        element={
          <RoleProtectedRoute allowedRoles={ATHLETE_ROLES}>
            <RecruitRedirect tab="offers" />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/following"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Following"><FollowingPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/academics"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Academics"><AcademicsPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/preferences"
        element={
          <RoleProtectedRoute allowedRoles={ATHLETE_ROLES}>
            <PageWithTitle title="Preferences"><PreferencesPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/photos"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Photos"><PhotosPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/social"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Social"><SocialPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach-profile"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'partner']}>
            <PageWithTitle title="Coach Profile"><CoachProfilePage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin-profile"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Admin Profile"><AdminProfilePage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/coach-academics"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'partner']}>
            <PageWithTitle title="Coach Academics"><CoachAcademicsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/coach-photos"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'partner']}>
            <PageWithTitle title="Coach Photos"><CoachPhotosPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/coach-social"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'partner']}>
            <PageWithTitle title="Coach Social"><CoachSocialPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/coach-videos"
        element={
          <RoleProtectedRoute allowedRoles={['coach', 'partner']}>
            <PageWithTitle title="Coach Videos"><CoachVideosPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/marketing/dashboard"
        element={
          <RoleProtectedRoute allowedRoles={['marketing', 'admin', 'internal_staff']}>
            <PageWithTitle title="Marketing Dashboard"><MarketingDashboardPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/marketing/schools"
        element={
          <RoleProtectedRoute allowedRoles={['marketing', 'admin', 'internal_staff']}>
            <PageWithTitle title="Marketing Schools"><MarketingSchoolsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/marketing/players"
        element={
          <RoleProtectedRoute allowedRoles={['marketing', 'admin', 'internal_staff']}>
            <PageWithTitle title="Marketing Players"><MarketingPlayersPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      {/* Export moved into the Players page (its Export CSV menu). Old links and bookmarks land there. */}
      <Route path="/marketing/export" element={<Navigate to="/marketing/players" replace />} />
      <Route
        path="/camp-data"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Camp Data"><CampDataPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/camps"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'coach', 'partner', 'athlete', 'player_parent', 'base_user']}>
            <PageWithTitle title="Camps"><CampsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/camps/:id/review"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff']}>
            <PageWithTitle title="Camp Review"><CampReviewPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/camps/checkin/:campId"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Camp Check-In"><CampCheckInPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/camps/checkin"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Camp Check-In"><CampCheckInPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/webinars"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'internal_staff', 'partner']}>
            <PageWithTitle title="Webinars"><WebinarsPage /></PageWithTitle>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <PageWithTitle title="Settings"><SettingsPage /></PageWithTitle>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<PageWithTitle title="Not Found"><NotFoundPage /></PageWithTitle>} />
    </Routes>
  );
}

function App() {
  // Capture on ANY landing route, not just /register — an ad may land on the
  // home page, a camp page or a shared profile, and the signup can happen
  // several navigations later.
  useEffect(() => {
    captureAttribution(window.location.search, document.referrer);
  }, []);

  return (
    <Router>
      <AuthProvider>
        <CatalogProvider>
          <ActingAthleteProvider>
            <EntitlementsProvider>
              <div className="App">
                <ActingAthleteFromLink>
                  <AppRoutes />
                </ActingAthleteFromLink>
              </div>
            </EntitlementsProvider>
          </ActingAthleteProvider>
        </CatalogProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
